import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { useAppSelector } from "@/store/store"
import { BookingServiceItem, KeysUsed } from "@okeys/shared-utils"
import { Euro, Gift, Key, Percent } from "lucide-react"
import React from "react"

// Type pour les groupes de promotions
type PromotionGroupData = {
  type: string
  count: number
  totalSavings: number
  icon: React.ReactNode
  label: string
}

const getPromotionGroupStats = (
  servicesWithPromo: BookingServiceItem[],
  keysUsed: KeysUsed[]
): {
  groups: PromotionGroupData[]
  totalSavings: number
  totalKeysUsed: number
  keysUse: number
} => {
  const groups: Record<string, PromotionGroupData> = {}
  let totalSavings = 0
  let totalKeysUsed = 0

  // Vérifications de sécurité
  const safeServicesWithPromo = servicesWithPromo || []
  const safeKeysUsed = keysUsed || []

  // Traitement des keys points
  if (safeKeysUsed.length > 0) {
    safeKeysUsed.forEach((key) => {
      // Trouver les services avec des réductions keys_points
      const servicesWithKeysDiscount = safeServicesWithPromo.filter(
        (service) =>
          service.serviceId === key.serviceId &&
          service.discountApplied?.type === "keys_points"
      )

      if (servicesWithKeysDiscount.length > 0) {
        const keysSavings = servicesWithKeysDiscount.reduce((sum, service) => {
          if (service.discountApplied?.type === "keys_points") {
            return (
              sum +
              (service.discountApplied.priceEffect.oldPrice -
                service.discountApplied.priceEffect.newPrice)
            )
          }
          return sum
        }, 0)

        if (!groups["keys_points"]) {
          groups["keys_points"] = {
            type: "keys_points",
            count: 0,
            totalSavings: 0,
            icon: <Key className="h-4 w-4" />,
            label: "Keys utilisées",
          }
        }

        groups["keys_points"].count += key.numberItemWithKeysUsed
        groups["keys_points"].totalSavings += keysSavings
        totalSavings += keysSavings
        totalKeysUsed += key.numberItemWithKeysUsed
      }
    })
  }

  // Traitement des autres promotions
  safeServicesWithPromo.forEach((service) => {
    if (
      service.discountApplied &&
      service.discountApplied.type !== "keys_points"
    ) {
      const promotionType = service.discountApplied.type

      if (!groups[promotionType]) {
        let icon = <Gift className="h-4 w-4" />
        let label = "Promotion"

        switch (promotionType) {
          case "promotion_percentage":
            icon = <Percent className="h-4 w-4" />
            label = "Réduction %"
            break
          case "promotion_fixed_amount":
            icon = <Euro className="h-4 w-4" />
            label = "Réduction fixe"
            break
          case "promotion_buy_x_get_y_discount":
            icon = <Gift className="h-4 w-4" />
            label = "Achetez X obtenez Y"
            break
          case "gift_card":
            icon = <Gift className="h-4 w-4" />
            label = "Carte cadeau"
            break
        }

        groups[promotionType] = {
          type: promotionType,
          count: 0,
          totalSavings: 0,
          icon,
          label,
        }
      }

      groups[promotionType].count += 1

      // Calculer l'économie selon le type de discount
      let savings = 0
      if ("priceEffect" in service.discountApplied) {
        savings =
          service.discountApplied.priceEffect.oldPrice -
          service.discountApplied.priceEffect.newPrice
      }

      groups[promotionType].totalSavings += savings
      totalSavings += savings
    }
  })

  const keysUse = safeServicesWithPromo.reduce((sum, service) => {
    if (service.discountApplied?.type === "keys_points") {
      return sum + service.discountApplied.keysUsed
    }
    return sum
  }, 0)

  return {
    groups: Object.values(groups),
    totalSavings,
    totalKeysUsed,
    keysUse,
  }
}

export const PromotionsSection: React.FC = () => {
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)
  const { groups, totalSavings, totalKeysUsed, keysUse } =
    getPromotionGroupStats(
      collectionInfo?.serviceWithPromo || [],
      collectionInfo?.keysUsed || []
    )

  return (
    <Card className="py-4 flex-1 bg-card border-border">
      <CardContent className="px-4">
        <h3 className="text-lg font-semibold text-card-foreground mb-3">
          Promotions appliquées
        </h3>

        {groups.length === 0 && (
          <div className="text-muted-foreground">
            Aucune promotion appliquée
          </div>
        )}

        {/* Statistiques globales */}
        {groups.length !== 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge
              variant="secondary"
              className="bg-blue-500/30 text-card-foreground"
            >
              <Euro className="h-3 w-3 mr-1" />
              Économie totale: {totalSavings.toFixed(2)}€
            </Badge>
            {totalKeysUsed > 0 && (
              <Badge
                variant="secondary"
                className="bg-primary/30 text-primary-foreground"
              >
                <Key className="h-3 w-3 mr-1" />
                {keysUse} Keys utilisées
              </Badge>
            )}
          </div>
        )}

        {/* Groupes de promotions */}
        {groups.length !== 0 && (
          <div className="space-y-2">
            {groups.map((group) => (
              <Card
                key={group.type}
                className={
                  group.type === "keys_points"
                    ? "bg-primary/10 border-primary py-0"
                    : "bg-blue-500/10 border-blue-800 py-0"
                }
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex-shrink-0">{group.icon}</div>
                      <div>
                        <p className={`text-md`}>{group.label}</p>
                        <div className="text-xs text-gray-600">
                          {`${group.count} services concernés`}
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold text-sm`}>
                      -{group.totalSavings.toFixed(2)}€
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
